import React, { ComponentType, useCallback, useEffect } from 'react';
import { styled, TextFieldProps } from '@mui/material';
import Button from '@/components/Button';
import { useFormik } from 'formik';
import ImageIcon from '@mui/icons-material/Image';
import { ImageField } from '@/components';
import { Form, FormButton, FormGroup, CustomTextField } from './common.controls';
import CustomCheckbox from './CustomCheckbox';
import t from '@/utils/translate';

export const ProjectNameField: ComponentType<Omit<TextFieldProps, 'variants'>> = styled(CustomTextField)({
  '&.MuiFormControl-root': {
    width: '63%',
    maxWidth: '400px',
  },
  '& .MuiInputBase-root': {
    width: '100%',
  },
});

const DeleteProjectBtn = styled(Button)`
  color: #FF8F73;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;
  box-sizing: border-box;
  padding: 9px 36px;
  width: 133px;
  height: 32px;
  border: 1px solid #C1C7D0;
  border-radius: 6px;
  white-space: nowrap;
`;

const TopRow = styled('div')({
  display: 'flex',
  flexSirection: 'row',
  flexWrap: 'nowrap',
  width: '100%',
});

const BottomRow = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '32px',
  padding: 0,
  flexGrow: 1,
});

export type ProjectFormData = {
  name: string,
  implicitProjectThumbnails: boolean,
  logoFile: File | null,
  logoPath: string | null,
  thumbnail: string | null,
};


export type ProjectCommonFormProps = {
  submitTitle: string,
  onSubmit: (values: ProjectFormData) => Promise<void>,
  initialValues: {
    name: string,
    logo: string | null,
    implicitProjectThumbnails: boolean,
    thumbnail: string | null,
  },
  disabled?: boolean,
  canDelete?: boolean,
  onDelete?: () => void,
};

export default ({
  onSubmit,
  initialValues,
  disabled = false,
  submitTitle,
  onDelete = () => {},
  canDelete = false,
}: ProjectCommonFormProps) => {
  const formik = useFormik({
    initialValues: {
      name: initialValues.name,
      implicitProjectThumbnails: initialValues.implicitProjectThumbnails,
      logoPath: initialValues.logo,
      logoFile: null,
      thumbnail: initialValues.thumbnail,
    },
    validate: (values: ProjectFormData) => {
      const errors: Record<string, string> = {};

      if (!values.name) {
        errors.name = t('project.validation.noName')
      } else if (values.name.length > 256) {
        errors.name = t('project.validation.nameTooLong')
      }

      return errors;
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, helpers) => {
      await onSubmit(values);
      helpers.resetForm({ values });
    },
  });

  useEffect(() => {
    formik.resetForm({
      values: {
        name: initialValues.name,
        implicitProjectThumbnails: initialValues.implicitProjectThumbnails,
        logoPath: initialValues.logo,
        logoFile: null,
        thumbnail: initialValues.thumbnail,
      },
      touched: {
        name: false,
        implicitProjectThumbnails: false,
        logoPath: false,
        logoFile: false
      },
      errors: {},
    });
  }, [ initialValues.name, initialValues.logo, initialValues.thumbnail, initialValues.implicitProjectThumbnails ]);

  const onLogoSetCallback = useCallback((file: File) => {
    formik.setFieldValue('logoFile', file);
    formik.setFieldValue('logoPath', URL.createObjectURL(file));
  }, [ formik ]);

  const onLogoClearCallback = useCallback(() => {
    if (formik.values.implicitProjectThumbnails) {
      formik.setFieldValue('thumbnail', null);
    } else {
      formik.setFieldValue('logoFile', null);
      formik.setFieldValue('logoPath', null);
    }
  }, [ formik ]);

  return (
    <>
      <Form onSubmit={formik.handleSubmit}>
        <TopRow>
          <FormGroup>
            {!disabled && (
              <div>
                <ImageField
                  title={formik.values.implicitProjectThumbnails
                    ? t('project.title.autoThumbnail')
                    : t('project.title.thumbnail')
                  }
                  source={
                    formik.values.implicitProjectThumbnails
                      ? formik.values.thumbnail || formik.values.logoPath
                      : formik.values.logoPath || formik.values.thumbnail
                  }
                  onSet={onLogoSetCallback}
                  onClear={onLogoClearCallback}
                  sizeH={300}
                  previewSizeV={140}
                  previewSizeH={260}
                  placeholder={
                    <ImageIcon
                      sx={{
                        fontSize: '93.35px',
                        color: '#EBECF0',
                        verticalAlign: 'inherit'
                      }}
                    />
                  }
                  disabled={formik.values.implicitProjectThumbnails}
                  opaque={
                    formik.values.implicitProjectThumbnails
                      ? !(!formik.values.thumbnail && !!formik.values.logoPath)
                      : !(!formik.values.logoPath && !!formik.values.thumbnail)
                  }
                  hint={
                    formik.values.implicitProjectThumbnails && !formik.values.thumbnail
                      ? t('project.hint.noAutoThumbnail')
                      : undefined
                  }
                  clearButtonHint={
                    formik.values.implicitProjectThumbnails
                      ? t('action.reset')
                      : undefined
                  }
                  clearable={
                    formik.values.implicitProjectThumbnails
                      ? !!formik.values.thumbnail
                      : !!formik.values.logoPath
                  }
                />
                <CustomCheckbox
                  style={{ marginTop: '20px', color: '#0052CC' }}
                  label={t('project.label.autoThumbnail')}
                  hint={t('project.label.autoThumbnail')}
                  checked={formik.values.implicitProjectThumbnails}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    formik.setFieldValue('implicitProjectThumbnails', event.target.checked);
                  }}
                  checkedColor="#0052CC"
                />
              </div>
            )}
          </FormGroup>

          <FormGroup>
            <ProjectNameField
              {...formik.getFieldProps('name')}
              disabled={disabled || formik.isSubmitting}
              label={t('project.label.name')}
              error={formik.touched.name && !!formik.errors.name}
              helperText={formik.touched.name && formik.errors.name}
              onChange={(e) => {
                formik.setFieldError('name', '');
                formik.setFieldValue('name', e.target.value);
              }}
              variant="outlined"
            />
          </FormGroup>
        </TopRow>
        {!disabled && (
          <BottomRow>
            <FormButton
              type="submit"
              disabled={formik.isSubmitting || !formik.dirty}
              variant="contained"
              loading={formik.isSubmitting}
            >
              {submitTitle}
            </FormButton>

            {canDelete && (
              <DeleteProjectBtn
                variant="contrast-text"
                title={t('project.action.delete')}
                onClick={onDelete}
              >
                {t('project.action.delete')}
              </DeleteProjectBtn>
            )}
          </BottomRow>
        )}
      </Form>
    </>
  );
};
